import { useCallback, useState } from 'react';

/**
 * A set of selected ids that survives filter changes, as in the web-app library: ids stay selected
 * while hidden, until cleared. Callers intersect it with their data, so a deleted doc drops out.
 */
export const useSelection = () => {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());

  const toggle = useCallback((id: string) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  // Select-all semantics: deselects the ids when every one is already selected, otherwise selects them all.
  const toggleMany = useCallback((ids: string[]) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      const allSelected = ids.length > 0 && ids.every((id) => next.has(id));
      for (const id of ids) {
        if (allSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }, []);

  const deselect = useCallback((ids: string[]) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      for (const id of ids) next.delete(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  return { selectedIds, toggle, toggleMany, deselect, clear };
};
